import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid, Switch,
  Text,
  Textarea,
  useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../constants/routes-path";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { trafficViolationService } from "../../../services/trafficViolationService";
import { checkErrors, hasError } from "../../../utils/forms-utils";

function TrafficViolationDefer({data}) {
  const textColor = useColorModeValue("gray.700", "white");
  const bgTextarea = useColorModeValue("white", "navy.900");
  const placeholderColor = useColorModeValue("gray.300", "gray.400");

  const { t} = useTranslation();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const { id } = useParams();

  const [inputError, setInputError] = useState({});

  const ids = useMemo(() => ({
    reason: "traffic-violation-defer-reason",
    warning: "traffic-violation-defer-warning",
    prescription: "traffic-violation-defer-prescription",
  }), []);

  const [formData, setFormData] = useState( {
    reason: "",
    warning: false,
    prescription: false,
  });

  const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

  const initFormData = (data) => ({
    [ids.reason]: {
      value: data?.motivo_deferimento ?? "",
      validations: [{ required: true }],
    },
    [ids.warning]: { value: data?.advertencia ?? false },
    [ids.prescription]: { value: data?.prescricao ?? false }
  });

  const handleInputChange = (e) => {
    const { id, type } = e.target;
    const value = type === "checkbox" ? e.target.checked : e.target.value;

    const newFormData = { ...formData, [id]: { ...formData[id], value: value } };
    setFormData(newFormData);
    setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
  };

  const fetchData = async () => {
    try {
      const data = await trafficViolationService.find(id);
      setFormData(initFormData(data));
    } catch (error) {
      showToast({ title: t(error), status: "error" });
      redirectToList();
    }
  }

  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setFormData(initFormData());
    }
  }, [id]);

  const redirectToList = () => {
    navigate(ROUTES.trafficViolationsPath);
  }

  const toDTO = () => ({
    motivo_deferimento: formData[ids.reason].value.trim(),
    advertencia: formData[ids.warning].value,
    prescricao: formData[ids.prescription].value,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isFormInvalid) return;

    try {
      const data = toDTO();
      await trafficViolationService.defer(id, data);

      showToast({ title: t("system.notifications.success"), status: "success" });
      redirectToList();
    } catch (error) {
      console.log(error)
      showToast({ title: t(error), status: "error" });
    }
  };

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const [errors, setErrors] = useState({});

  return (
    <Flex direction="column" minH="89vh" align="center" pt={{sm: "120px", md: "75px"}}>
      <Card mt={"80px"}>
        <CardHeader mb="40px">
          <Flex direction="column">
            <Text color={textColor} fontSize="lg" fontWeight="bold" mb="3px">
              {t("traffic-violation-defer.form.title")}
            </Text>
            <Text color="gray.400" fontWeight="normal" fontSize="sm">
              {t("traffic-violation-defer.form.subtitle")}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Flex direction="column" w="100%">
              <Grid gap="24px">
                <FormControl isRequired isInvalid={errors[ids.reason]}>
                  <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                    {t("traffic-violation-defer.form.fields.reason.label")}
                  </FormLabel>
                  <Textarea
                    minH="120px"
                    name="reason"
                    id={ids.reason}
                    value={formData[ids.reason]?.value}
                    onChange={handleInputChange}
                    bg={bgTextarea}
                    _placeholder={{color: placeholderColor}}
                    placeholder={t("traffic-violation-defer.form.fields.reason.placeholder")}
                    fontSize="xs"
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0" fontSize="xs" fontWeight="bold">
                    {t("traffic-violation-defer.form.fields.warning.label")}
                  </FormLabel>
                  <Switch
                    id={ids.warning}
                    isChecked={formData[ids.warning]?.value}
                    onChange={handleInputChange}
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0" fontSize="xs" fontWeight="bold">
                    {t("traffic-violation-defer.form.fields.prescription.label")}
                  </FormLabel>
                  <Switch
                    id={ids.prescription}
                    isChecked={formData[ids.prescription]?.value}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </Grid>
              <Flex justify="space-between">
                <Button
                  variant="light"
                  fontSize="sm"
                  alignSelf="flex-end"
                  mt="24px"
                  w="100px"
                  h="35px"
                  onClick={redirectToList}
                >
                  {t("system.actions.back")}
                </Button>
                <Button
                  variant="dark"
                  fontSize="sm"
                  alignSelf="flex-end"
                  type="submit"
                  mt="24px"
                  w="100px"
                  h="35px"
                  isDisabled={isFormInvalid}
                  _hover={isFormInvalid ? {} : undefined}
                >
                  {t("system.actions.save")}
                </Button>
              </Flex>
            </Flex>
          </form>
        </CardBody>
      </Card>
    </Flex>
  );
}

export default TrafficViolationDefer;